import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  commands as allCommands,
  formatChord,
  groupOrder,
  type Command,
  type CommandContext,
  type CommandGroup,
} from '../lib/keymap';
import { accountHue, api, type ThreadDetail, type ThreadView } from '../lib/api';
import { useUiStore } from '../lib/store';
import { useKeyboard } from '../hooks/useKeyboard';
import {
  flushPendingSends,
  hasPendingSend,
  intentToStoreDraft,
  requestPopoutCompose,
  subscribePendingSend,
  undoLastPendingSend,
} from './Composer';
import { focusMailSearch, getRecentSearches, runMailSearch } from './SearchBar';
import { ToastOutlet, toast } from './Toast';
import styles from './CommandPalette.module.css';

export type PaletteMode = 'palette' | 'cheatsheet';

export type PaletteEntry = {
  id: string;
  label: string;
  hint?: string;
  group: string;
  /** Canonical chord strings, rendered as <kbd> chips. */
  keys?: string[];
  /** Hex hue for a leading dot — used by account rows. */
  accent?: string;
  run: () => void;
};

export type CommandPaletteProps = {
  open: boolean;
  mode?: PaletteMode;
  ctx: CommandContext;
  onClose: () => void;
  /** Host-supplied dynamic rows (labels, folders, saved views…). */
  extras?: PaletteEntry[];
  /** Defaults to the searches SearchBar has persisted. */
  recentSearches?: string[];
  onRunSearch?: (query: string) => void;
  commands?: Command[];
};

/* ----------------------------- install prompt ----------------------------- */

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let stashedInstallPrompt: InstallPromptEvent | null = null;
const installListeners = new Set<(value: InstallPromptEvent | null) => void>();

function isInstallPromptEvent(event: Event): event is InstallPromptEvent {
  return 'prompt' in event && typeof (event as InstallPromptEvent).prompt === 'function';
}

if (typeof window !== 'undefined') {
  // Registered at import time: the browser fires this once, very early, and a
  // prompt that is not captured is gone for the session.
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    if (!isInstallPromptEvent(event)) return;
    stashedInstallPrompt = event;
    for (const listener of installListeners) listener(event);
  });
  window.addEventListener('appinstalled', () => {
    stashedInstallPrompt = null;
    for (const listener of installListeners) listener(null);
  });
}

export function useInstallPrompt(): InstallPromptEvent | null {
  const [prompt, setPrompt] = useState<InstallPromptEvent | null>(stashedInstallPrompt);
  useEffect(() => {
    installListeners.add(setPrompt);
    return () => {
      installListeners.delete(setPrompt);
    };
  }, []);
  return prompt;
}

/* --------------------------------- fuzzy ---------------------------------- */

type FuzzyHit = { score: number; positions: number[] };

/**
 * Subsequence match with bonuses for consecutive runs and word starts, so
 * "arch" ranks "Archive" above "Search all accounts".
 */
export function fuzzyMatch(query: string, text: string): FuzzyHit | null {
  if (query === '') return { score: 0, positions: [] };
  const haystack = text.toLowerCase();
  const needle = query.toLowerCase();
  const positions: number[] = [];
  let score = 0;
  let cursor = 0;
  let lastIndex = -2;

  for (const char of needle) {
    if (char === ' ') continue;
    const index = haystack.indexOf(char, cursor);
    if (index === -1) return null;
    positions.push(index);
    score += 1;
    if (index === lastIndex + 1) score += 6;
    if (index === 0) score += 10;
    else if (/[\s\-_/.:]/.test(haystack[index - 1] ?? '')) score += 5;
    lastIndex = index;
    cursor = index + 1;
  }

  // Shorter labels win ties; earlier first match wins too.
  score -= Math.min(text.length, 40) * 0.1;
  score -= (positions[0] ?? 0) * 0.2;
  return { score, positions };
}

function Highlight({ text, positions }: { text: string; positions: number[] }): ReactElement {
  if (positions.length === 0) return <>{text}</>;
  const marked = new Set(positions);
  return (
    <>
      {Array.from(text).map((char, index) =>
        marked.has(index) ? (
          <b key={index} className={styles.match}>
            {char}
          </b>
        ) : (
          <span key={index}>{char}</span>
        ),
      )}
    </>
  );
}

function Keys({ chord }: { chord: string }): ReactElement {
  const segments = formatChord(chord);
  return (
    <span className={styles.keys}>
      {segments.map((tokens, segmentIndex) => (
        <span key={segmentIndex} className={styles.keySegment}>
          {segmentIndex > 0 ? <span className={styles.then}>then</span> : null}
          {tokens.map((token, tokenIndex) => (
            <kbd key={tokenIndex} className={styles.kbd}>
              {token}
            </kbd>
          ))}
        </span>
      ))}
    </span>
  );
}

/* -------------------------------- palette --------------------------------- */

function CommandPaletteView({
  open,
  mode = 'palette',
  ctx,
  onClose,
  extras,
  recentSearches,
  onRunSearch,
  commands = allCommands,
}: CommandPaletteProps): ReactElement | null {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const restoreFocusTo = useRef<Element | null>(null);
  const installPrompt = useInstallPrompt();

  useEffect(() => {
    if (!open) return;
    restoreFocusTo.current = document.activeElement;
    setQuery('');
    setActiveIndex(0);
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open, mode]);

  const close = useCallback(() => {
    onClose();
    const target = restoreFocusTo.current;
    if (target instanceof HTMLElement) target.focus();
  }, [onClose]);

  const entries = useMemo<PaletteEntry[]>(() => {
    if (!open || mode !== 'palette') return [];
    const rows: PaletteEntry[] = [];

    for (const command of commands) {
      if (command.paletteHidden) continue;
      // Documentation-only bindings belong on the cheat sheet, not in a list of
      // things you can click.
      const invoke = command.run;
      if (!invoke) continue;
      if (command.when && !command.when(ctx)) continue;
      rows.push({
        id: `cmd:${command.id}`,
        label: command.label,
        hint: command.keywords,
        group: command.group,
        keys: command.keys.slice(0, 1),
        run: () => invoke(ctx),
      });
    }

    // "All inboxes" already comes from the keymap; only the real accounts are
    // dynamic, and they carry the Alt+N slot their static twin is bound to.
    const enabled = ctx.accounts.filter((a) => a.enabled);
    enabled.forEach((account, index) => {
      rows.push({
        id: `account:${account.id}`,
        label: account.displayName || account.email,
        hint: account.email,
        group: 'Accounts',
        accent: accountHue(account.id, ctx.accounts),
        keys: index < 8 ? [`Alt+${index + 1}`] : undefined,
        run: () => ctx.setAccountFilter(account.id),
      });
    });

    const searches = recentSearches ?? getRecentSearches();
    for (const search of searches.slice(0, 6)) {
      rows.push({
        id: `search:${search}`,
        label: search,
        hint: 'recent search',
        group: 'Recent searches',
        run: () => onRunSearch?.(search),
      });
    }

    if (installPrompt) {
      rows.push({
        id: 'app:install',
        label: 'Install app',
        hint: 'add Kiln to this device',
        group: 'Application',
        run: () => {
          void installPrompt.prompt();
        },
      });
    }

    if (extras) rows.push(...extras);
    return rows;
  }, [open, mode, commands, ctx, recentSearches, onRunSearch, installPrompt, extras]);

  const results = useMemo(() => {
    const trimmed = query.trim();
    if (trimmed === '') {
      return entries.map((entry) => ({ entry, positions: [] as number[] }));
    }
    const scored: { entry: PaletteEntry; positions: number[]; score: number }[] = [];
    for (const entry of entries) {
      const labelHit = fuzzyMatch(trimmed, entry.label);
      const hintHit = entry.hint ? fuzzyMatch(trimmed, entry.hint) : null;
      const groupHit = fuzzyMatch(trimmed, entry.group);
      const best = labelHit ?? hintHit ?? groupHit;
      if (!best) continue;
      // A hit that only landed in the keywords or group is a weaker signal.
      const score = labelHit ? best.score : best.score - 8;
      scored.push({ entry, positions: labelHit ? labelHit.positions : [], score });
    }
    scored.sort((a, b) => b.score - a.score);
    return scored;
  }, [entries, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    const node = listRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    node?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, results]);

  const runIndex = useCallback(
    (index: number) => {
      const hit = results[index];
      if (!hit) return;
      close();
      hit.entry.run();
    },
    [results, close],
  );

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent) => {
      // The palette owns its keys entirely; the global dispatcher must not see them.
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        close();
        return;
      }
      if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        event.stopPropagation();
        close();
        return;
      }
      if (mode !== 'palette') return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        event.stopPropagation();
        setActiveIndex((i) => (results.length === 0 ? 0 : (i + 1) % results.length));
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        event.stopPropagation();
        setActiveIndex((i) => (results.length === 0 ? 0 : (i - 1 + results.length) % results.length));
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        runIndex(activeIndex);
      }
    },
    [mode, results.length, activeIndex, runIndex, close],
  );

  if (!open) return null;

  const grouped: { group: string; rows: { entry: PaletteEntry; positions: number[] }[] }[] = [];
  for (const hit of results) {
    const bucket = grouped.find((g) => g.group === hit.entry.group);
    if (bucket) bucket.rows.push(hit);
    else grouped.push({ group: hit.entry.group, rows: [hit] });
  }
  let flatIndex = -1;

  return (
    <div className={styles.scrim} onMouseDown={close} role="presentation">
      <div
        className={mode === 'cheatsheet' ? `${styles.panel} ${styles.wide}` : styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label={mode === 'cheatsheet' ? 'Keyboard shortcuts' : 'Command palette'}
        onMouseDown={(event) => event.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        {mode === 'palette' ? (
          <>
            <div className={styles.searchRow}>
              <svg className={styles.searchIcon} viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
                <circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
                <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <input
                ref={inputRef}
                className={styles.input}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Type a command…"
                aria-label="Search commands"
                autoComplete="off"
                spellCheck={false}
              />
              <kbd className={styles.kbd}>Esc</kbd>
            </div>

            <div className={styles.list} ref={listRef} role="listbox" aria-label="Commands">
              {results.length === 0 ? (
                <div className={styles.empty}>No matching command</div>
              ) : (
                grouped.map((bucket) => (
                  <div key={bucket.group} className={styles.group}>
                    <div className={styles.groupLabel}>{bucket.group}</div>
                    {bucket.rows.map(({ entry, positions }) => {
                      flatIndex += 1;
                      const index = flatIndex;
                      const active = index === activeIndex;
                      const chord = entry.keys?.[0];
                      return (
                        <button
                          key={entry.id}
                          type="button"
                          role="option"
                          aria-selected={active}
                          data-active={active}
                          className={styles.row}
                          onMouseMove={() => setActiveIndex(index)}
                          onClick={() => runIndex(index)}
                          tabIndex={-1}
                        >
                          {entry.accent ? (
                            <span className={styles.dot} style={{ background: entry.accent }} aria-hidden="true" />
                          ) : null}
                          <span className={styles.label}>
                            <Highlight text={entry.label} positions={positions} />
                          </span>
                          {entry.hint ? <span className={styles.hint}>{entry.hint}</span> : null}
                          {chord ? <Keys chord={chord} /> : null}
                        </button>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          </>
        ) : (
          <CheatSheet commands={commands} ctx={ctx} onClose={close} />
        )}
      </div>
    </div>
  );
}

function CheatSheet({
  commands,
  ctx,
  onClose,
}: {
  commands: Command[];
  ctx: CommandContext;
  onClose: () => void;
}): ReactElement {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  const groups = groupOrder
    .map((group: CommandGroup) => ({
      group,
      rows: commands.filter((c) => c.group === group && !c.paletteHidden && (!c.when || c.when(ctx))),
    }))
    .filter((g) => g.rows.length > 0);

  return (
    <div className={styles.cheatsheet}>
      <div className={styles.cheatHeader}>
        <span className={styles.cheatTitle}>Keyboard shortcuts</span>
        <button ref={closeRef} type="button" className={styles.cheatClose} onClick={onClose}>
          Close
          <kbd className={styles.kbd}>Esc</kbd>
        </button>
      </div>
      <div className={styles.cheatColumns}>
        {groups.map(({ group, rows }) => (
          <section key={group} className={styles.cheatGroup}>
            <h3 className={styles.groupLabel}>{group}</h3>
            {rows.map((command) => (
              <div key={command.id} className={styles.cheatRow}>
                <span className={styles.label}>{command.label}</span>
                <span className={styles.cheatKeys}>
                  {command.keys.map((chord, index) => (
                    <span key={chord} className={styles.keyAlt}>
                      {index > 0 ? <span className={styles.then}>or</span> : null}
                      <Keys chord={chord} />
                    </span>
                  ))}
                </span>
              </div>
            ))}
          </section>
        ))}
      </div>
    </div>
  );
}



/* ------------------------------ store binding ----------------------------- */

type UndoEntry = { label: string; run: () => void };

function tomorrowMorningEpochSeconds(): number {
  const target = new Date();
  target.setDate(target.getDate() + 1);
  target.setHours(8, 0, 0, 0);
  return Math.round(target.getTime() / 1000);
}

/**
 * The one command context the shell uses. It also registers the global scope
 * with the dispatcher, which is why this component is mounted for the whole
 * session rather than only while the palette is on screen.
 */
export function CommandPalette(overrides: Partial<CommandPaletteProps> = {}): ReactElement {
  const queryClient = useQueryClient();

  const accountFilter = useUiStore((s) => s.accountFilter);
  const setAccountFilter = useUiStore((s) => s.setAccountFilter);
  const setView = useUiStore((s) => s.setView);
  const setCategory = useUiStore((s) => s.setCategory);
  const selectedThreadKey = useUiStore((s) => s.selectedThreadKey);
  const setSelectedThreadKey = useUiStore((s) => s.setSelectedThreadKey);
  const paletteOpen = useUiStore((s) => s.paletteOpen);
  const setPaletteOpen = useUiStore((s) => s.setPaletteOpen);
  const settingsOpen = useUiStore((s) => s.settingsOpen);
  const setSettingsOpen = useUiStore((s) => s.setSettingsOpen);
  const drawerOpen = useUiStore((s) => s.drawerOpen);
  const setDrawerOpen = useUiStore((s) => s.setDrawerOpen);
  const composerOpen = useUiStore((s) => s.composer.open);
  const openComposer = useUiStore((s) => s.openComposer);
  const closeComposer = useUiStore((s) => s.closeComposer);
  const toggleFocusMode = useUiStore((s) => s.toggleFocusMode);

  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: () => api.accounts(), staleTime: 60_000 });
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 5 * 60_000 });

  const pendingSend = useSyncExternalStore(subscribePendingSend, hasPendingSend, hasPendingSend);

  const [cheatOpen, setCheatOpen] = useState(false);
  const undoStack = useRef<UndoEntry[]>([]);

  const accounts = accountsQuery.data?.accounts ?? [];
  const aiEnabled = meQuery.data?.aiEnabled === true;

  const ctx = useMemo<CommandContext>(() => {
    const invalidate = (key: string | null) => {
      void queryClient.invalidateQueries({ queryKey: ['threads'] });
      void queryClient.invalidateQueries({ queryKey: ['search'] });
      if (key) void queryClient.invalidateQueries({ queryKey: ['thread', key] });
    };

    const currentThread = (key: string): ThreadView | null =>
      queryClient.getQueryData<ThreadDetail>(['thread', key])?.thread ?? null;

    const run = (
      key: string,
      action: Parameters<typeof api.threadAction>[1],
      body: Parameters<typeof api.threadAction>[2],
      done: { label: string; undo?: UndoEntry },
    ) => {
      void api
        .threadAction(key, action, body)
        .then(() => invalidate(key))
        .catch(() => {
          invalidate(key);
          toast({ title: `${done.label} failed`, tone: 'danger' });
        });
      if (done.undo) undoStack.current.push(done.undo);
      toast({
        title: done.label,
        action: done.undo ? { label: 'Undo', keys: 'Z', onClick: done.undo.run } : undefined,
      });
    };

    const withThread = (fn: (key: string) => void) => {
      if (selectedThreadKey) fn(selectedThreadKey);
    };

    const archive = (restore?: boolean) =>
      withThread((key) =>
        run(key, 'archive', { restore: restore === true }, {
          label: restore === true ? 'Moved to Inbox' : 'Archived',
          undo: { label: 'archive', run: () => archive(restore !== true) },
        }),
      );

    return {
      accounts,
      accountFilter,
      aiEnabled,
      selection: { threadKey: selectedThreadKey, count: selectedThreadKey ? 1 : 0 },
      hasPendingSend: pendingSend,

      goBack: () => {
        if (cheatOpen) setCheatOpen(false);
        else if (paletteOpen) setPaletteOpen(false);
        else if (settingsOpen) setSettingsOpen(false);
        else if (composerOpen) closeComposer();
        else if (drawerOpen) setDrawerOpen(false);
        else if (selectedThreadKey) setSelectedThreadKey(null);
      },
      goTo: (target) => {
        switch (target) {
          case 'inbox':
            setCategory(null);
            setView('inbox');
            break;
          case 'done':
            setCategory(null);
            setView('archive');
            break;
          case 'newsletters':
            setView('inbox');
            setCategory('newsletters');
            break;
          case 'receipts':
            setView('inbox');
            setCategory('receipts');
            break;
          default:
            setCategory(null);
            setView(target);
        }
      },
      setAccountFilter,
      toggleFocusMode,

      archive,
      trash: () =>
        withThread((key) =>
          // The contract has no un-trash, so this one is offered without an undo.
          run(key, 'trash', undefined, { label: 'Moved to Trash' }),
        ),
      snooze: () =>
        withThread((key) =>
          run(key, 'snooze', { until: tomorrowMorningEpochSeconds() }, {
            label: 'Snoozed until tomorrow',
            undo: {
              label: 'snooze',
              run: () => {
                void api.threadAction(key, 'snooze', { until: null }).then(() => invalidate(key));
              },
            },
          }),
        ),
      toggleStar: () =>
        withThread((key) => {
          const value = !(currentThread(key)?.flagged ?? false);
          run(key, 'star', { value }, {
            label: value ? 'Starred' : 'Unstarred',
            undo: {
              label: 'star',
              run: () => {
                void api.threadAction(key, 'star', { value: !value }).then(() => invalidate(key));
              },
            },
          });
        }),
      toggleRead: () =>
        withThread((key) => {
          const value = (currentThread(key)?.unreadCount ?? 1) > 0;
          run(key, 'read', { value }, { label: value ? 'Marked read' : 'Marked unread' });
        }),
      undo: () => {
        const restored = undoLastPendingSend();
        if (restored) {
          openComposer(intentToStoreDraft(restored));
          toast({ title: 'Send cancelled' });
          return;
        }
        const entry = undoStack.current.pop();
        if (entry) entry.run();
      },

      compose: (popout) => {
        if (popout) requestPopoutCompose();
        openComposer({ mode: 'new' });
      },
      reply: (all, popout) => {
        if (!selectedThreadKey) return;
        if (popout) requestPopoutCompose();
        openComposer({ mode: all ? 'reply-all' : 'reply', threadKey: selectedThreadKey });
      },
      forward: (popout) => {
        if (!selectedThreadKey) return;
        if (popout) requestPopoutCompose();
        openComposer({ mode: 'forward', threadKey: selectedThreadKey });
      },
      flushPendingSend: () => flushPendingSends(),

      openPalette: () => setPaletteOpen(true),
      openCheatSheet: () => setCheatOpen(true),
      // The field lives in the list pane; with none mounted the palette is the
      // honest fallback rather than a key that does nothing.
      focusSearch: (allAccounts) => {
        if (!focusMailSearch(allAccounts)) setPaletteOpen(true);
      },
    };
  }, [
    accounts,
    accountFilter,
    aiEnabled,
    pendingSend,
    selectedThreadKey,
    cheatOpen,
    paletteOpen,
    settingsOpen,
    composerOpen,
    drawerOpen,
    queryClient,
    setAccountFilter,
    setCategory,
    setView,
    setSelectedThreadKey,
    setPaletteOpen,
    setSettingsOpen,
    setDrawerOpen,
    openComposer,
    closeComposer,
    toggleFocusMode,
  ]);

  useKeyboard('global', ctx);

  const open = overrides.open ?? (cheatOpen || paletteOpen);
  const mode: PaletteMode = overrides.mode ?? (cheatOpen ? 'cheatsheet' : 'palette');

  return (
    <>
      <CommandPaletteView
        open={open}
        mode={mode}
        ctx={ctx}
        onClose={() => {
          setCheatOpen(false);
          setPaletteOpen(false);
        }}
        onRunSearch={(query) => {
          if (runMailSearch(query)) setPaletteOpen(false);
        }}
        {...overrides}
      />
      <ToastOutlet />
    </>
  );
}

export default CommandPalette;
